import { useCallback, useState, FC } from 'react';

import EditableField from '../../../EditableField';
import { CaretBox, FolderName, FolderRowInfo, NestedFoldersWrap, NewFolderWrapper, Title } from '../../Wrapper';
import { Folder, RenameFolderProps } from '../../types';

export type FolderRowProps = {
  id: string | number;
  name: string;
  folders: Folder[];
  activeRow: string | number;
  setActive: (id: string | number) => void;
  onSelectRow: (id: string | number) => void;
  onExpandFolder: (id: string | number) => void;
  isInputFocused: (id: number | string) => boolean;
  onRenameFolder: (props: RenameFolderProps) => void;
  isRenamed?: boolean;
  isExpanded?: boolean;
};

const FolderRow: FC<FolderRowProps> = ({
  id,
  name,
  folders,
  activeRow,
  isRenamed,
  setActive,
  isExpanded,
  onSelectRow,
  isInputFocused,
  onExpandFolder,
  onRenameFolder
}) => {
  const nestedFoldersExist = !!folders.length;
  const showNestedFolders = isExpanded && nestedFoldersExist;

  const [folderWasCreated, setFolderWasCreated] = useState(false);

  const isNew = !isRenamed && !folderWasCreated && isInputFocused(id);

  const handleCarretClick = useCallback(() => onExpandFolder(id), [onExpandFolder, id]);
  const handleEditableFieldChange = useCallback(
    (newName: string) => {
      onRenameFolder({ id, newName, oldName: name });
      setFolderWasCreated(true);
    },
    [name, id, onRenameFolder, setFolderWasCreated]
  );
  const handleRowClick = useCallback(() => onSelectRow(id), [id, onSelectRow]);
  const handleFolderNameClick = useCallback(() => setActive(id), [id, setActive]);

  return (
    <>
      <FolderRowInfo data-folder-id={id} isActive={activeRow === id} onClick={handleRowClick}>
        <CaretBox isExpanded={!!isExpanded} visible={nestedFoldersExist} onClick={handleCarretClick} />
        {!isNew ? (
          <FolderName onClick={handleFolderNameClick}>
            <Title>{name}</Title>
          </FolderName>
        ) : (
          <NewFolderWrapper>
            <EditableField isActive={isNew} initialValue={name} onChange={handleEditableFieldChange} />
          </NewFolderWrapper>
        )}
      </FolderRowInfo>

      {showNestedFolders && (
        <NestedFoldersWrap>
          {folders.map(f => (
            <FolderRow
              key={f.id}
              {...{
                ...f,
                onSelectRow,
                onExpandFolder,
                onRenameFolder,
                isInputFocused,
                activeRow,
                setActive
              }}
            />
          ))}
        </NestedFoldersWrap>
      )}
    </>
  );
};

export default FolderRow;
